/**
 * What a tool reports about its own steps while one call is still running.
 *
 * `tool.progress` carries an opaque `data` — the agents framework routes it
 * and never interprets it, because it genuinely cannot know what any given
 * tool is doing. This is the one shape the chat UI *does* understand, so a
 * tool that wants its steps drawn in its own card emits this and everything
 * else is ignored. Same arrangement `ui-action` already has for tool results:
 * the framework stays ignorant, the shape is published, surfaces opt in.
 *
 * A tool with several concurrent reads is one call to the model and several
 * steps to the person waiting. This is how the second becomes visible.
 */
export interface ToolStep {
  /** Stable id. Repeated reports for the same id update it in place. */
  id: string;
  /**
   * What to show for this step *now*. The tool owns the wording and sends it
   * afresh with each report, so the tense carries the state on its own:
   * "Reviewing your price history" while it runs, "Reviewed your price
   * history" once it has. No second field, and the row still reads correctly
   * in a screenshot or to a reader who cannot see the animation.
   */
  label?: string;
  /**
   * `pending` is work this call intends to do and has not started. It exists so
   * a tool that knows its whole plan up front can draw it up front — a walk
   * that reveals each step only as it begins gives no sense of how much is
   * left. It must not shimmer and must carry no glyph: a queued row that looks
   * active is worse than no row, because then every row claims to be working.
   *
   * A pre-drawn plan can be wrong — a branch gets abandoned, a store runs out
   * of things to read — so whatever emits `pending` owes the rows a resolution
   * when the call ends. A row left pending forever reads as still coming.
   *
   * `failed` is deliberately not a kind of finished. A region we could not
   * read must not tick green — three labels that always go green look
   * identical and lie on exactly the stores where the merchant needs to know.
   *
   * `unavailable` is just as deliberately not a kind of `failed`. A step that
   * looked and found nothing to show has not gone wrong, and a store where
   * that is the normal answer — no branding on a headless theme, no orders on
   * a new install — must not be told something broke every time it loads.
   * Collapsing the two was the bug: the merchant got an error for the ordinary
   * case, and the real errors stopped standing out.
   */
  status: "pending" | "running" | "ok" | "unavailable" | "failed";
  /**
   * Why it did not succeed, in words a merchant would recognise. Expected on
   * both `unavailable` and `failed` — a row that stops short without saying
   * why is the one thing worse than not drawing it.
   */
  reason?: string;
  /**
   * Which glyph names this step, e.g. `"catalog"`, `"brand"`, `"pricing"`.
   *
   * A name rather than a component, because this crosses the wire — the tool
   * that knows what the step *was* picks it, and the surface decides what that
   * name looks like. An unknown name falls back to the surface's default, so a
   * tool naming a glyph the frontend has not mapped yet still renders.
   *
   * Deliberately not `status`. The frames give each step a topical icon — a
   * page, a brush, a target — while `status` says whether it worked, so a
   * surface showing both needs them to be two fields.
   */
  icon?: string;
}

/**
 * Model-adjacent input arriving over a network: check it rather than cast it.
 * An unrecognised payload is ignored, which is what "opaque to the framework"
 * has to mean in practice.
 */
export const isToolStep = (value: unknown): value is ToolStep => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as {
    id?: unknown;
    label?: unknown;
    status?: unknown;
    reason?: unknown;
    icon?: unknown;
  };

  if (typeof candidate.id !== "string" || candidate.id === "") {
    return false;
  }
  if (
    candidate.status !== "pending" &&
    candidate.status !== "running" &&
    candidate.status !== "ok" &&
    candidate.status !== "unavailable" &&
    candidate.status !== "failed"
  ) {
    return false;
  }
  if (candidate.label !== undefined && typeof candidate.label !== "string") {
    return false;
  }
  if (candidate.icon !== undefined && typeof candidate.icon !== "string") {
    return false;
  }
  if (candidate.reason !== undefined && typeof candidate.reason !== "string") {
    return false;
  }
  return true;
};
